import { File, FileText, Folder, Lock, Table, Trash2 } from 'lucide-react'

import { Badge, Checkbox, Spinner } from '@/components/ui/misc'
import { cn } from '@/lib/cn'
import { formatBytes, formatDate } from '@/lib/format'
import type { FolderRow, ObjectMeta, ObjectRow } from '@/lib/api'

function iconFor(name: string) {
  const ext = name.split('.').pop()?.toLowerCase()
  if (ext === 'csv' || ext === 'tsv' || ext === 'xlsx') return Table
  if (ext === 'txt' || ext === 'md' || ext === 'json' || ext === 'log') return FileText
  return File
}

export function ObjectTable({
  folders,
  objects,
  metas,
  selected,
  onToggle,
  onToggleAll,
  onOpenFolder,
  onOpenObject,
  onDeleteFolder,
  loading,
}: {
  folders: FolderRow[]
  objects: ObjectRow[]
  metas: Map<string, ObjectMeta>
  selected: Set<string>
  onToggle: (key: string, shiftKey: boolean) => void
  onToggleAll: () => void
  onOpenFolder: (prefix: string) => void
  onOpenObject: (key: string) => void
  /** Omitted when the caller lacks the delete permission. */
  onDeleteFolder?: ((folder: FolderRow) => void) | undefined
  loading: boolean
}) {
  const allSelected = objects.length > 0 && objects.every((o) => selected.has(o.key))
  const someSelected = objects.some((o) => selected.has(o.key))

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[42rem] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
            <th className="w-10 px-4 py-2.5">
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected && !allSelected}
                onChange={onToggleAll}
                disabled={objects.length === 0}
                aria-label="Select all objects on this page"
              />
            </th>
            <th className="px-2 py-2.5 font-medium">Name</th>
            <th className="w-28 px-2 py-2.5 text-right font-medium">Size</th>
            <th className="w-36 px-2 py-2.5 font-medium">Modified</th>
            <th className="w-40 px-4 py-2.5 font-medium">Status</th>
          </tr>
        </thead>

        <tbody>
          {folders.map((folder) => (
            <tr
              key={folder.prefix}
              className="group cursor-pointer border-b border-border transition-colors hover:bg-muted/60"
              onClick={() => onOpenFolder(folder.prefix)}
            >
              <td className="px-4 py-2.5" />
              <td className="px-2 py-2.5">
                <span className="flex items-center gap-2 font-medium">
                  <Folder className="h-4 w-4 shrink-0 text-accent" />
                  <span className="truncate">{folder.name}</span>
                </span>
              </td>
              <td className="px-2 py-2.5 text-right text-muted-foreground">—</td>
              <td className="px-2 py-2.5 text-muted-foreground">—</td>
              <td className="px-4 py-2.5 text-right">
                {onDeleteFolder && (
                  <button
                    type="button"
                    // The row itself navigates into the folder, so this must not
                    // bubble.
                    onClick={(e) => {
                      e.stopPropagation()
                      onDeleteFolder(folder)
                    }}
                    title="Delete this folder (only works when it is empty)"
                    aria-label={`Delete folder ${folder.name}`}
                    className="rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-danger/10 hover:text-danger focus:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger/40 group-hover:opacity-100"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </td>
            </tr>
          ))}

          {objects.map((object) => {
            const meta = metas.get(object.key)
            const Icon = iconFor(object.name)
            const isSelected = selected.has(object.key)

            return (
              <tr
                key={object.key}
                className={cn(
                  'cursor-pointer border-b border-border transition-colors hover:bg-muted/60',
                  isSelected && 'bg-accent/8',
                )}
                onClick={() => onOpenObject(object.key)}
              >
                <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={isSelected}
                    onChange={(e) =>
                      onToggle(object.key, (e.nativeEvent as MouseEvent).shiftKey ?? false)
                    }
                    aria-label={`Select ${object.name}`}
                  />
                </td>
                <td className="px-2 py-2.5">
                  <span className="flex items-center gap-2">
                    <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="truncate">{object.name}</span>
                  </span>
                </td>
                <td className="px-2 py-2.5 text-right tabular text-muted-foreground">
                  {formatBytes(object.size)}
                </td>
                <td className="px-2 py-2.5 tabular text-muted-foreground">
                  {formatDate(object.lastModified)}
                </td>
                <td className="px-4 py-2.5">
                  {/* Undefined means the HEAD batch has not returned yet;
                      false means it has and the object is genuinely unlocked. */}
                  {meta === undefined ? (
                    <span className="text-xs text-muted-foreground">…</span>
                  ) : meta.locked ? (
                    <Badge tone="danger" title={meta.lockReason ?? undefined}>
                      <Lock className="h-3 w-3" />
                      {meta.retentionHold ? 'Hold' : 'Retained'}
                    </Badge>
                  ) : null}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>

      {loading && (
        <div className="flex justify-center py-8">
          <Spinner className="h-5 w-5" />
        </div>
      )}
    </div>
  )
}
